import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { WarningIcon } from './Icons';
import OpportunitiesSection from './OpportunitiesSection';
import { BACKEND_URL } from '../lib/backendUrl';
import './Dashboard.css';

const API_URL = BACKEND_URL;

interface Announcement {
  id: string;
  title: string;
  description: string;
  image?: string;
  createdBy?: string;
  createdAt?: string;
}

const formatAnnouncementSender = (createdBy?: string) => {
  const sender = (createdBy || '').trim();

  if (!sender || sender.toLowerCase() === 'admin' || sender.toLowerCase() === 'super admin') {
    return '{ Admin }';
  }

  const clubAdminMatch = sender.match(/^(.*)\s+club admin$/i);
  if (clubAdminMatch) {
    const clubName = clubAdminMatch[1].trim();
    return `{{${clubName}} Club Admin}`;
  }

  if (sender.toLowerCase().includes('club admin')) {
    return `{${sender}}`;
  }

  return '{ Admin }';
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setError(null);
        setLoading(true);
        // Create a clean axios instance without auth headers for public endpoint
        const publicAxios = axios.create({
          baseURL: API_URL,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        // Fetch public announcements without authentication
        const response = await publicAxios.get('/api/announcements/public');
        
        // Handle API response - could be array or wrapped in object
        const announcementData = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || response.data?.announcements || []);
        
        setAnnouncements(announcementData);
      } catch (error: any) {
        console.error('Failed to fetch announcements:', error);
        // If endpoint doesn't exist or returns 401/403/404, just show empty state
        if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 404) {
          console.warn('Announcements endpoint not available or not authorized, showing empty state');
          setAnnouncements([]);
          setError(null); // Don't show error, just show empty state
        } else {
          // Show user-friendly error message for other errors
          const errorMessage = error.response?.data?.error || 'Failed to load announcements';
          console.error('Error details:', errorMessage);
          setError(errorMessage);
          setAnnouncements([]);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchMyRegistrations = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        // Parallelize API calls for better performance
        const [regRes, eventsRes] = await Promise.all([
          axios.get(`${API_URL}/api/registrations/me`, config),
          axios.get(`${API_URL}/api/events`, config)
        ]);
        
        setRegistrations(regRes.data || []);
        setEvents(eventsRes.data || []);
      } catch (err) {
        console.error('Failed to fetch registrations', err);
      }
    };

    fetchDashboardData();
    fetchMyRegistrations();
  }, [user]);

  const latestAnnouncement = announcements[0];

  // Memoize event lookup for better performance
  const getEventDetails = useMemo(() => {
    const eventMap = new Map(events.map(e => [e.id, e]));
    return (eventId: string) => eventMap.get(eventId);
  }, [events]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="student-dashboard-shell">
      <div className="student-dashboard-grid">
        <aside className="dashboard-left-column">
          <section className="dash-panel system-alert">
            <h3 className="dash-panel-title magenta">SYSTEM_ALERT</h3>
            {latestAnnouncement ? (
              <>
                <p className="dash-panel-main-text">{latestAnnouncement.title}</p>
                <p className="dash-panel-subtext">{latestAnnouncement.description}</p>
              </>
            ) : (
              <p className="dash-panel-subtext">No alerts available right now.</p>
            )}
            <span className="dash-link">ACCESS TERMINAL _&gt;</span>
          </section>

          <section className="dash-panel xp-protocols">
            <h3 className="dash-panel-title cyan">XP_EARNING_PROTOCOLS</h3>
            <div className="xp-list">
              <div className="xp-row"><span>EVENT_ATTENDANCE</span><strong>+50 XP</strong></div>
              <div className="xp-row"><span>VOLUNTEERING</span><strong>+100 XP</strong></div>
              <div className="xp-row"><span>JOIN_CLUB</span><strong>+20 XP</strong></div>
              <div className="xp-row"><span>DISC_PARTICIPATION</span><strong>+10 XP</strong></div>
            </div>
          </section>
        </aside>

        <section className="dashboard-center-column">
          <section className="announcement-terminal dash-panel">
            <div className="terminal-header">
              <span>[ANNOUNCEMENT_TERMINAL]</span>
              <span className="terminal-live">_LIVE_UPDATE</span>
            </div>

            <div className="terminal-body">
              {loading && <p className="terminal-line">&gt; Initializing announcement stream...</p>}

              {!loading && error && (
                <p className="terminal-line error-line">
                  &gt; <WarningIcon size={14} color="#ff006e" /> {error}
                </p>
              )}

              {!loading && !error && announcements.length === 0 && (
                <p className="terminal-line">&gt; No announcements published yet by admins.</p>
              )}

              {!loading && !error && announcements.length > 0 && announcements.slice(0, 10).map((announcement) => (
                <p key={announcement.id} className="terminal-line">
                  <span className="terminal-time">
                    [{announcement.createdAt ? new Date(announcement.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}]
                  </span>{' '}
                  <span className="terminal-sender">{formatAnnouncementSender(announcement.createdBy)}:</span> {announcement.title} - {announcement.description}
                </p>
              ))}

              <p className="terminal-cursor">_</p>
            </div>
          </section>

          <section className="dash-panel live-pulse-panel">
            <h2 className="live-pulse-title">
              <span className="cyan-block"></span> LIVE CAMPUS PULSE
            </h2>
            <div className="pulse-cards">
              <article className="pulse-card cyan-border group">
                <div className="pulse-card-header">
                  <span className="pulse-chip cyan-text uppercase">
                    <span className="pulse-dot"></span> ACTIVE NOW
                  </span>
                  <span className="pulse-id">Live_Stream.01</span>
                </div>
                <h4>Robotics Lab Demo</h4>
                <p>Live demonstration of the autonomous drone fleet in Hall B.</p>
                <div className="pulse-card-footer">
                  <button className="join-protocol-btn cyan-bg pulse-live">JOIN PROTOCOL</button>
                </div>
              </article>
              <article className="pulse-card purple-border group">
                <div className="pulse-card-header">
                  <span className="pulse-chip purple-text uppercase">TRENDING GUILDS</span>
                  <span className="pulse-id new-tag uppercase">HOT</span>
                </div>
                <h4>Photography Guild</h4>
                <p>+15 New Nodes this week. 24 Active Discussions.</p>
              </article>
            </div>
          </section>

          <OpportunitiesSection />
        </section>

        <aside className="dashboard-right-column" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '100vh' }}>
          <section className="dash-panel wallet-panel" style={{ flex: '1', minHeight: '300px', overflowY: 'auto' }}>
            <h3 className="dash-panel-title magenta" style={{ position: 'sticky', top: 0, backgroundColor: 'rgba(12, 17, 24, 0.95)', zIndex: 10, paddingBottom: '10px', paddingTop: '5px' }}>MY_WALLET</h3>
            {registrations.length === 0 ? (
              <p className="dash-panel-subtext" style={{ padding: '15px' }}>No tickets yet. Register for events to fill your wallet!</p>
            ) : (
              registrations.map((reg) => {
                const eventDetails = getEventDetails(reg.event_id || reg.eventId);
                return (
                  <div key={reg.id} className="ticket-item">
                    <p className="ticket-id">TICKET_ID: {reg.id.substring(0, 8).toUpperCase()}</p>
                    <h4>{eventDetails ? eventDetails.name : 'Loading Event...'}</h4>
                    <button className="ticket-btn">VIEW QR ACCESS</button>
                  </div>
                );
              })
            )}
          </section>

          <section className="dash-panel events-panel" style={{ flex: '1', minHeight: '300px', overflowY: 'auto' }}>
            <h3 className="dash-panel-title cyan" style={{ position: 'sticky', top: 0, backgroundColor: 'rgba(12, 17, 24, 0.95)', zIndex: 10, paddingBottom: '10px', paddingTop: '5px' }}>REGISTERED_EVENTS</h3>
            {registrations.length === 0 ? (
              <p className="dash-panel-subtext" style={{ padding: '15px' }}>No events registered yet.</p>
            ) : (
              registrations.map((reg) => {
                const eventDetails = getEventDetails(reg.event_id || reg.eventId);
                if (!eventDetails) return null;
                const eventDate = new Date(eventDetails.date);
                return (
                  <div key={`event-${reg.id}`} className="event-mini-item">
                    <div className="event-date-mini">
                      <span>{eventDate.getDate()}</span>
                      <small>{eventDate.toLocaleString('default', { month: 'short' }).toUpperCase()}</small>
                    </div>
                    <div>
                      <h5>{eventDetails.name}</h5>
                      <p>{eventDetails.location || 'TBA'}</p>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </aside>
      </div>
    </motion.div>
  );
};

export default Dashboard;
